<!-- Saved-search new matches — grouped subsections + opportunity/award cards (Chunk 4) -->
<template>
  <v-card
    id="saved-search-new-matches-section"
    v-if="visible"
    :style="sectionStyle"
    :class="[`${baseClass} mb-6 mt-6`, 'dashboard-section-chip-filter-scroll-target']"
    elevation="0"
  >
    <section-header
      :base-class="baseClass"
      :title="title"
      :subtitle="subtitle"
      :icon="headerIcon"
    >
      <template #controls>
        <div
          :class="[
            `${baseClass}-filter-control-wrap`,
            'dashboard-section-filter-control-wrap',
            { 'dashboard-section-filter-control-highlight': filterControlHighlightActive },
          ]"
        >
          <v-select
            v-model="selectedNoticeType"
            :class="`${baseClass}-notice-type-select`"
            :items="noticeTypeFilterOptions"
            item-text="text"
            item-value="value"
            dense
            outlined
            hide-details
            label="Notice type"
            :menu-props="{ contentClass: 'notice-type-menu' }"
          />
        </div>
      </template>
    </section-header>

    <div v-if="digestLoading" class="text-center py-8">
      <v-progress-circular indeterminate color="#ff1463" />
    </div>

    <div v-else-if="!hasMatches" class="text-center py-8 grey--text">
      {{ emptyMessage }}
    </div>

    <div v-else-if="!hasFilteredMatches" class="text-center py-8 grey--text">
      {{ filterEmptyMessage }}
    </div>

    <v-card-text v-else :class="`${baseClass}-matches-container`">
      <div :class="`${baseClass}-matches-list`">
        <div
          v-for="(row, index) in paginatedItems"
          :key="getRowKey(row, index)"
          :class="`${baseClass}-match-item`"
        >
          <h3
            v-if="shouldShowGroupHeader(row, index)"
            :class="`${baseClass}-subsection-heading`"
          >
            {{ groupTitle(row) }}
          </h3>

          <saved-search-opportunity-card
            v-if="isOpportunityRow(row)"
            :row="row"
            @filter-notice-type="setNoticeTypeFilter"
          >
            <template #actions>
              <v-btn
                small
                outlined
                color="primary"
                :class="`${baseClass}-action-btn`"
                :loading="row.loading"
                :disabled="row.loading"
                @click.stop.prevent="handleAddToPipeline(row)"
              >
                <v-icon small left>mdi-bookmark-plus</v-icon>
                Add to Pipeline
              </v-btn>
            </template>
          </saved-search-opportunity-card>

          <saved-search-award-card
            v-else
            :row="row"
            @filter-notice-type="setNoticeTypeFilter"
          >
            <template #actions>
              <v-btn
                small
                outlined
                color="primary"
                :class="`${baseClass}-action-btn`"
                :loading="row.loading"
                :disabled="row.loading"
                @click.stop.prevent="handleAddToPipeline(row)"
              >
                <v-icon small left>mdi-bookmark-plus</v-icon>
                Add to Pipeline
              </v-btn>
            </template>
          </saved-search-award-card>
        </div>
      </div>

      <section-pagination-footer
        :show="showPagination"
        :text="paginationText"
        :can-prev="canGoToPreviousPage"
        :can-next="canGoToNextPage"
        @prev="goToPreviousPage"
        @next="goToNextPage"
      />
    </v-card-text>
  </v-card>
</template>

<script>
import PaginationMixin from '../mixins/PaginationMixin.js';
import SectionChipFilterFeedbackMixin from '../mixins/SectionChipFilterFeedbackMixin.js';
import SectionHeader from '../common/SectionHeader.vue';
import SectionPaginationFooter from '../common/SectionPaginationFooter.vue';
import SavedSearchOpportunityCard from './SavedSearchOpportunityCard.vue';
import SavedSearchAwardCard from './SavedSearchAwardCard.vue';
import {
  DISPLAY_GROUP_TITLES,
  NOTICE_TYPE_FILTER_OPTIONS,
  dispositionDisplayGroup,
  flattenSavedSearchNewMatchesInGroupOrder,
  isOpportunityDisplayGroup,
} from '../../utils/savedSearchDisplayGroups.js';

export default {
  name: 'SavedSearchNewMatchesSection',

  mixins: [PaginationMixin, SectionChipFilterFeedbackMixin],

  components: {
    SectionHeader,
    SectionPaginationFooter,
    SavedSearchOpportunityCard,
    SavedSearchAwardCard,
  },

  props: {
    /** Normalized saved-search match rows from useSavedSearchNewMatches */
    matches: {
      type: Array,
      default: () => [],
    },
    digestLoading: {
      type: Boolean,
      default: false,
    },
    visible: {
      type: Boolean,
      default: true,
    },
    sectionStyle: {
      type: String,
      default: '',
    },
    pageSize: {
      type: Number,
      default: 10,
    },
  },

  data() {
    return {
      baseClass: 'saved-search-new-matches-section',
      title: 'New Notices Matching Your Saved Searches',
      subtitle: 'Notices from your saved searches, new on Tendertrace in the last 30 days',
      headerIcon: 'mdi-bell-ring',
      emptyMessage: 'No new notices matching your saved searches found',
      filterEmptyMessage: 'No notices found for this notice type',
      selectedNoticeType: 'all',
      noticeTypeFilterOptions: NOTICE_TYPE_FILTER_OPTIONS,
    };
  },

  computed: {
    hasMatches() {
      return Array.isArray(this.matches) && this.matches.length > 0;
    },

    filteredMatches() {
      if (!this.hasMatches) return [];
      if (this.selectedNoticeType === 'all') return this.matches;
      return this.matches.filter(
        (row) => dispositionDisplayGroup(row.disposition) === this.selectedNoticeType
      );
    },

    hasFilteredMatches() {
      return this.filteredMatches.length > 0;
    },

    paginationItems() {
      return flattenSavedSearchNewMatchesInGroupOrder(this.filteredMatches);
    },
  },

  watch: {
    selectedNoticeType() {
      this.currentPage = 1;
    },
  },

  methods: {
    getRowKey(row, index) {
      return row?.tender_id || `saved-search-match-${index}`;
    },

    shouldShowGroupHeader(row, index) {
      if (this.selectedNoticeType !== 'all') return false;
      const group = dispositionDisplayGroup(row.disposition);
      if (index === 0) return true;
      const previousRow = this.paginatedItems[index - 1];
      return dispositionDisplayGroup(previousRow.disposition) !== group;
    },

    groupTitle(row) {
      return DISPLAY_GROUP_TITLES[dispositionDisplayGroup(row.disposition)];
    },

    isOpportunityRow(row) {
      return isOpportunityDisplayGroup(dispositionDisplayGroup(row.disposition));
    },

    handleAddToPipeline(row) {
      this.$emit('add-to-pipeline', row);
    },

    setNoticeTypeFilter(noticeType) {
      if (!noticeType || noticeType === 'all') return;
      this.selectedNoticeType = noticeType;
      this.applyChipFilterFeedback('saved-search-new-matches-section');
    },
  },
};
</script>

<style scoped>
.saved-search-new-matches-section-notice-type-select {
  max-width: 220px;
}

.saved-search-new-matches-section-matches-container {
  padding: 0 24px 16px;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.saved-search-new-matches-section-matches-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.saved-search-new-matches-section-match-item {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.saved-search-new-matches-section-subsection-heading {
  margin: 20px 0 10px;
  font-size: 16px;
  color: #374151;
  font-weight: 600;
}

.saved-search-new-matches-section-subsection-heading:first-child {
  margin-top: 0;
}

.saved-search-new-matches-section-action-btn {
  font-size: 13px;
  text-transform: capitalize;
}
</style>

<style>
/* Pagination — aligned with other dashboard sections */
.saved-search-new-matches-section .pagination-container {
  border-top: 1px solid #e5e7eb !important;
  background-color: #f9fafb !important;
  padding: 16px !important;
  padding-top: 8px !important;
  display: flex !important;
  width: 100% !important;
  min-width: 100% !important;
  max-width: 100% !important;
}

.saved-search-new-matches-section .pagination-container .pagination-content {
  display: flex !important;
  justify-content: space-between !important;
  align-items: center !important;
  width: 100% !important;
  min-width: 100% !important;
  max-width: 100% !important;
  flex: 1 1 100% !important;
}

.saved-search-new-matches-section .pagination-info {
  font-size: 0.875rem !important;
  color: #6b7280 !important;
}

.saved-search-new-matches-section .pagination-controls {
  display: flex !important;
  gap: 8px !important;
}

.dashboard-section-chip-filter-scroll-target {
  scroll-margin-top: 96px;
}

.dashboard-section-filter-control-wrap {
  border-radius: 4px;
}

.dashboard-section-filter-control-highlight {
  animation: dashboard-section-filter-control-highlight 1.5s ease-in-out;
}

@keyframes dashboard-section-filter-control-highlight {
  0%,
  100% {
    box-shadow: none;
  }

  50% {
    box-shadow: 0 0 0 3px rgba(255, 20, 99, 0.35);
  }
}
</style>
